/**
 * Emporia predictive search runtime.
 * Required hooks: emporia-predictive-search with [data-endpoint] and
 * [data-section-id], plus [data-emporia-predictive-input/results/status];
 * response options use [data-emporia-predictive-option].
 * Emits: emporia:predictive-search:open, :close, :loaded, and :error.
 */

(() => {
  'use strict';

  if (customElements.get('emporia-predictive-search')) return;

  class EmporiaPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-emporia-predictive-input]');
      this.results = this.querySelector('[data-emporia-predictive-results]');
      this.status = this.querySelector('[data-emporia-predictive-status]');
      this.endpoint = this.dataset.endpoint;
      this.sectionId = this.dataset.sectionId || 'emporia-predictive-search';
      this.minimumCharacters = Number.parseInt(this.dataset.minimumCharacters, 10) || 2;
      this.activeIndex = -1;
      this.abortController = null;
      this.requestNumber = 0;
      this.lastLoadedQuery = '';

      if (!this.input || !this.results || !this.endpoint) return;

      this.debouncedInput = this.debounce(this.handleInput.bind(this), 250);
      this.onInput = () => {
        this.abortController?.abort();
        this.debouncedInput();
      };
      this.onFocus = this.handleFocus.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('focus', this.onFocus);
      this.input?.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    emit(name, detail = {}) {
      this.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail
        })
      );
    }

    debounce(callback, delay) {
      let timer;
      const debounced = (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => callback(...args), delay);
      };
      debounced.cancel = () => window.clearTimeout(timer);
      return debounced;
    }

    handleInput() {
      const query = this.input.value.trim();
      if (query.length < this.minimumCharacters) {
        this.abortController?.abort();
        this.lastLoadedQuery = '';
        this.close({ clear: true });
        return;
      }

      this.load(query);
    }

    handleFocus() {
      const query = this.input.value.trim();
      if (query.length < this.minimumCharacters) return;

      if (query === this.lastLoadedQuery && this.results.innerHTML.trim()) {
        this.open();
      } else {
        this.load(query);
      }
    }

    handleDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    handleKeydown(event) {
      const options = this.options;

      switch (event.key) {
        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          this.open();
          this.setActive(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          this.open();
          this.setActive(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
          break;
        case 'Home':
          if (!options.length || this.results.hidden) return;
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          if (!options.length || this.results.hidden) return;
          event.preventDefault();
          this.setActive(options.length - 1);
          break;
        case 'Enter':
          if (this.activeIndex < 0 || !options[this.activeIndex]) return;
          event.preventDefault();
          options[this.activeIndex].click();
          break;
        case 'Escape':
          if (this.results.hidden) return;
          event.preventDefault();
          this.close();
          this.input.focus();
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    get options() {
      return [...this.results.querySelectorAll('[data-emporia-predictive-option]')];
    }

    async load(query) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const requestNumber = ++this.requestNumber;
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('resources[type]', 'query,product');
      url.searchParams.set('resources[limit]', '5');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      this.input.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Predictive search request failed (${response.status})`);
        }

        const html = await response.text();
        if (
          requestNumber !== this.requestNumber ||
          query !== this.input.value.trim()
        ) {
          return;
        }

        this.render(html);
        this.lastLoadedQuery = query;
        this.open();

        const resultCount = Number.parseInt(
          this.results
            .querySelector('[data-emporia-predictive-results-content]')
            ?.dataset.resultCount,
          10
        ) || 0;
        this.emit('emporia:predictive-search:loaded', { query, resultCount });
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.close({ clear: true });
        this.emit('emporia:predictive-search:error', { error, query });
        console.error('[Emporia predictive search]', error);
      } finally {
        if (requestNumber === this.requestNumber) {
          this.input.removeAttribute('aria-busy');
        }
      }
    }

    render(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const section = parsed.getElementById(`shopify-section-${this.sectionId}`);
      const content =
        section?.querySelector('[data-emporia-predictive-results-content]') ||
        parsed.querySelector('[data-emporia-predictive-results-content]');

      if (!content) throw new Error('Predictive search response was empty');

      this.results.replaceChildren(content.cloneNode(true));
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      this.options.forEach((option, index) => {
        option.id = `${this.results.id}-Option-${index + 1}`;
        option.setAttribute('aria-selected', 'false');
      });

      const label = this.results
        .querySelector('[data-emporia-predictive-result-label]')
        ?.textContent.trim();
      if (this.status) this.status.textContent = label || '';
    }

    setActive(index) {
      const options = this.options;
      if (!options.length) return;

      const nextIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        option.setAttribute('aria-selected', String(optionIndex === nextIndex));
      });

      this.activeIndex = nextIndex;
      const activeOption = options[nextIndex];
      this.input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    }

    open() {
      if (!this.results.innerHTML.trim()) return;
      const wasOpen = !this.results.hidden;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (!wasOpen) this.emit('emporia:predictive-search:open');
    }

    close({ clear = false } = {}) {
      this.debouncedInput?.cancel?.();
      this.abortController?.abort();
      const wasOpen = !this.results.hidden;
      this.results.hidden = true;
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;

      this.options.forEach((option) => option.setAttribute('aria-selected', 'false'));

      if (clear) {
        this.results.replaceChildren();
        if (this.status) this.status.textContent = '';
      }

      if (wasOpen) this.emit('emporia:predictive-search:close');
    }
  }

  customElements.define('emporia-predictive-search', EmporiaPredictiveSearch);
})();
